/**
 * D-25 / R08 — the invitation acceptance page.
 *
 * THE RULE THIS PAGE EXISTS TO ENFORCE: loading a URL never accepts anything.
 * An invite link lands here and the page asks the server what the invitation
 * IS — who sent it and to which role — through POST /api/v1/join/preview, a
 * read that writes no row and no audit entry. Nothing is accepted until you
 * press Accept. A link that accepted on load would be claimed by a mail
 * scanner, a link preview or a prefetch before the invited person ever saw it.
 *
 * THE TOKEN IS HANDLED LIKE A CREDENTIAL, because that is what it is:
 *   - it is read once from the query string and immediately removed from the
 *     address bar with history.replaceState, so it does not sit in browser
 *     history, a screenshot or a shared URL;
 *   - it is held in sessionStorage (this tab only, cleared when the tab
 *     closes) so it survives the round trip through sign-in;
 *   - it is deleted as soon as it has been used or has definitively failed;
 *   - it is sent in a request BODY, never a URL path, so it never reaches an
 *     access log, a proxy log or a referrer header.
 *
 * THE INVITED ADDRESS IS NEVER MASKED HERE. The server sends it already
 * masked (`s•••••@example.com`) and this page renders that string verbatim.
 * Masking in the browser would be cosmetic — the full address would still be
 * in the response, readable by exactly the person it is being kept from.
 *
 * The return path after sign-in is pinned to /join and nothing else, so a
 * crafted link cannot use this page as an open redirect.
 *
 * WHY THIS PAGE DRAWS ITS OWN CHROME: it is mounted OUTSIDE <AppShell>
 * (routes.tsx), deliberately — an invited person has no role yet, and the
 * shell's nav, bell and profile menu are all things they cannot use. But
 * outside the shell it inherits no header and no card, which is why the
 * first version of this page rendered as browser-default HTML. So it
 * renders the shared `.header` bar itself (logo only, no nav) and the
 * app's `.form-card`, and every control is the shared <Button>. The first
 * screen a new user ever sees should look like the product.
 */
import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';
import {
  v2,
  ApiError,
  parseApiErrorBody,
  type InvitationMismatchData,
  type InvitationPreview,
} from '@/api/v2Client';
import { useNavigate } from 'react-router-dom';
import { useUserPermissions } from '@/hooks/useUserPermissions';
import { startSignOut } from '@/lib/auth/signOut';
import { INVITE_TOKEN_KEY, JOIN_PATH } from '@/lib/invite/inviteSession';
import { Wordmark } from '@/components/Wordmark';
import { Button } from '@/components/shared/Button';
import { buttonStyle } from '@/components/shared/buttonStyle';

/**
 * sessionStorage key — this tab only, and cleared the moment it is spent.
 * The same value the login page needs, so it is imported rather than spelled
 * out twice. Token handling below is unchanged.
 */
const TOKEN_KEY = INVITE_TOKEN_KEY;
/** The ONLY place sign-in may return to. Never taken from a query parameter. */
const RETURN_PATH = JOIN_PATH;
/** Shown when the server did not disclose the invited address. */
const UNNAMED_ADDRESS = 'the invited address';

type Retryable = 'preview' | 'accept';

type State =
  | { kind: 'loading' }
  | { kind: 'no-token' }
  | { kind: 'ready' }
  | { kind: 'accepting' }
  | { kind: 'accepted'; roleName?: string }
  | { kind: 'signed-out' }
  | { kind: 'wrong-account'; invitedEmail?: string }
  | { kind: 'unverified' }
  | { kind: 'expired' }
  | { kind: 'gone' }
  | { kind: 'switched-off' }
  | { kind: 'error'; message: string; retry: Retryable };

/** Read the token from the URL once, then scrub it out of the address bar. */
function captureTokenFromUrl(): string | null {
  const params = new URLSearchParams(window.location.search);
  const fromUrl = params.get('token');
  if (fromUrl) {
    try {
      window.sessionStorage.setItem(TOKEN_KEY, fromUrl);
    } catch {
      // Private mode or blocked storage: the token still works for THIS
      // attempt, it just will not survive a sign-in round trip.
    }
    params.delete('token');
    const clean = params.toString();
    window.history.replaceState(
      {},
      '',
      `${window.location.pathname}${clean ? `?${clean}` : ''}`,
    );
    return fromUrl;
  }
  try {
    return window.sessionStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

function forgetToken(): void {
  try {
    window.sessionStorage.removeItem(TOKEN_KEY);
  } catch { /* nothing to do */ }
}

/**
 * ONE translation from an API refusal to a screen, used by both the preview
 * and the accept. The two calls run the same checks on the server, so they
 * must land on the same screens here — a second copy would drift.
 *
 * Returns null when the error is not an ApiError at all (a transport
 * failure), which is retryable and keeps the token.
 */
function stateForError(err: unknown, retry: Retryable): State | null {
  if (!(err instanceof ApiError)) return null;
  const parsed = parseApiErrorBody(err);
  const code = parsed?.error;
  switch (err.status) {
    case 401:
      // Keep the token: it is needed after sign-in.
      return { kind: 'signed-out' };
    case 403: {
      if (code === 'FeatureDisabled') return { kind: 'switched-off' };
      if (code === 'InvitationEmailMismatch') {
        // KEEP the token: the right person may sign in on this tab. The
        // address below arrives already masked from the server.
        const invited = (parsed?.data as InvitationMismatchData | undefined)?.invitedEmail;
        return { kind: 'wrong-account', ...(invited ? { invitedEmail: invited } : {}) };
      }
      return { kind: 'unverified' };
    }
    case 410:
      forgetToken();
      return { kind: 'expired' };
    case 404:
    case 409:
      forgetToken();
      return { kind: 'gone' };
    default:
      // Retryable: the token stays.
      return { kind: 'error', message: 'Something went wrong. Please try again.', retry };
  }
}

/** Card copy. The muted sub-line keeps the token it already used. */
const bodyText = { margin: '0 0 8px 0', fontSize: 14, color: 'var(--text)' } as const;
const mutedText = { margin: '0 0 4px 0', fontSize: 13, color: 'var(--text-muted)' } as const;
/** The action row, so every state spaces its button identically. */
const actions = { marginTop: 20, display: 'flex', justifyContent: 'center' } as const;

/**
 * The status glyph at the top of the card. EXISTING GLYPHS ONLY — this app has
 * no icon set, it uses these characters, and the lock is the very one the
 * Access denied card uses. Decorative: every state also says what it is in
 * words, and the glyph is aria-hidden.
 */
function statusIcon(kind: State['kind']): { glyph: string; color: string } | null {
  switch (kind) {
    // Nothing to announce while the answer is still unknown.
    case 'loading':
      return null;
    case 'ready':
    case 'accepting':
    case 'signed-out':
      return { glyph: '\u{1F511}', color: 'var(--accent)' };
    case 'accepted':
      return { glyph: '\u2705', color: 'var(--green)' };
    case 'wrong-account':
      return { glyph: '\u{1F512}', color: 'var(--red)' };
    case 'unverified':
    case 'switched-off':
      return { glyph: '\u26D4', color: 'var(--red)' };
    // no-token, expired, gone, error.
    default:
      return { glyph: '\u26A0', color: 'var(--amber)' };
  }
}

export function JoinPage(): ReactElement {
  const [state, setState] = useState<State>({ kind: 'loading' });
  // What the SERVER said about the invitation. The page shows what it was
  // told and never invents a name or a role.
  const [invite, setInvite] = useState<InvitationPreview>({});
  const perms = useUserPermissions();
  const navigate = useNavigate();
  /** Guards against a double-click and against React 18 double-effects. */
  const inFlight = useRef(false);
  const token = useRef<string | null>(null);

  const preview = useCallback(async (): Promise<void> => {
    const t = token.current;
    if (!t) { setState({ kind: 'no-token' }); return; }
    if (inFlight.current) return;
    inFlight.current = true;
    setState({ kind: 'loading' });
    try {
      const res = await v2.users.previewInvitation(t);
      setInvite(res ?? {});
      setState({ kind: 'ready' });
    } catch (err) {
      setState(
        stateForError(err, 'preview')
        ?? { kind: 'error', message: 'Something went wrong. Please try again.', retry: 'preview' },
      );
    } finally {
      inFlight.current = false;
    }
  }, []);

  useEffect(() => {
    token.current = captureTokenFromUrl();
    // LOADING NEVER ACCEPTS. This is a read: the server writes nothing for it,
    // not even an audit row. There is deliberately no accept call here.
    void preview();
  }, [preview]);

  const accept = useCallback(async (): Promise<void> => {
    if (inFlight.current) return;        // double-click safe
    const t = token.current;
    if (!t) { setState({ kind: 'no-token' }); return; }

    inFlight.current = true;
    setState({ kind: 'accepting' });
    try {
      const res = await v2.users.acceptInvitation(t);
      forgetToken();
      // The join HAS happened. Say so first, so nothing that comes after can
      // turn a success into an error message.
      setState({ kind: 'accepted', ...(res?.roleName ? { roleName: res.roleName } : {}) });
      // The role just granted changes what this person may do, so the
      // permission cache must not keep serving the pre-accept answer.
      //
      // NOT A BUG FIX, and I am not claiming one: useUserPermissions' fetchMe
      // catches everything and refresh() cannot currently reject, so the catch
      // below is unreachable today. The point is that this page no longer
      // DEPENDS on that staying true. Were a refresh ever to throw, reporting
      // it as a failed join would be a lie — the role is granted — and would
      // send the person to retry a token the server has already spent.
      try {
        await perms.refresh();
      } catch {
        /* a stale cache heals on the next load; the join still happened */
      }
    } catch (err) {
      setState(
        stateForError(err, 'accept')
        ?? { kind: 'error', message: 'Something went wrong. Please try again.', retry: 'accept' },
      );
    } finally {
      inFlight.current = false;          // retry stays possible
    }
  }, [perms]);

  // REVIEW #2 — both buttons used to promise something they did not do.
  //
  // `Sign in` went to `/?returnTo=/join` and `Sign out and switch account` to
  // `/?signout=1`. LoginPage reads NEITHER query parameter: it reads
  // `location.state.from` and never signs anyone out. So after signing in the
  // person landed on /home although this page says they will come back here,
  // and the wrong-account person was bounced to /home still signed in as the
  // wrong account — with no way to do the one thing the screen asked of them.
  //
  // Real sign-in path only: with AUTH_ENABLED=false there is no MSAL session,
  // which is why dev never showed it.
  const signIn = useCallback((): void => {
    // Router navigation, not window.location.assign: LoginPage reads
    // `location.state.from`, and a full page load threw that state away.
    // The return path is a CONSTANT — never a query parameter, so this page
    // cannot be used as an open redirect.
    navigate('/', { state: { from: RETURN_PATH } });
  }, [navigate]);

  const signOut = useCallback((): void => {
    // The app's real sign-out, so this actually ends the wrong session. The
    // post-logout redirect comes back HERE so the right person can accept;
    // the token stays in sessionStorage across it, because MSAL clears its
    // own cache keys and this is the same tab.
    if (!startSignOut(RETURN_PATH)) {
      // Dev (AUTH_ENABLED=false): no MSAL session to end. Keep what this
      // button did before rather than inventing something — dev identities
      // are switched with the dev switcher, not by signing out.
      navigate('/');
    }
  }, [navigate]);

  const icon = statusIcon(state.kind);

  return (
    <>
      {/* The shared navy header bar. LOGO ONLY: no nav, no bell, no profile
          menu — this person is not a member yet, so there is nothing behind
          those controls for them. Not a link to /home either, for the same
          reason. */}
      <header className="header">
        <div className="logo">
          <Wordmark />
        </div>
      </header>

      <main
        data-testid="join-page"
        style={{ minHeight: 'calc(100vh - 60px)', background: 'var(--bg)', padding: '32px 24px' }}
      >
        {/* `.form-card` is the app's card class: surface, 1px border, 10px
            radius, the standard subtle shadow. 560px is the Access denied
            card's width, so this sits comfortably at ~760px and narrower. */}
        <section
          className="form-card"
          data-testid="join-card"
          style={{ maxWidth: 560, margin: '40px auto', textAlign: 'center' }}
        >
          {icon && (
            <div
              aria-hidden="true"
              data-testid="join-status-icon"
              style={{ fontSize: 40, marginBottom: 12, color: icon.color }}
            >
              {icon.glyph}
            </div>
          )}
          <h1 style={{ fontSize: 22, margin: '0 0 12px 0', color: 'var(--accent)' }}>
            Join AutoeCTD
          </h1>

          {state.kind === 'loading' && (
            <p data-testid="join-loading" style={bodyText}>Checking your invitation…</p>
          )}

          {state.kind === 'no-token' && (
            <section data-testid="join-no-token">
              <p style={bodyText}>This page needs an invitation link.</p>
              <p style={mutedText}>
                Open the link from your invitation email, or ask an administrator to send a new one.
              </p>
            </section>
          )}

          {(state.kind === 'ready' || state.kind === 'accepting') && (
            <section data-testid="join-ready">
              <p style={bodyText}>
                You have been invited to join AutoeCTD
                {invite.invitedBy ? <> by <strong>{invite.invitedBy}</strong></> : null}
                {invite.roleName ? <> as <strong>{invite.roleName}</strong></> : null}.
              </p>
              <p style={mutedText}>
                Nothing has been accepted yet. Choose Accept to join.
              </p>
              <div style={actions}>
                <Button
                  data-testid="join-accept-btn"
                  onClick={() => { void accept(); }}
                  disabled={state.kind === 'accepting'}
                >
                  {state.kind === 'accepting' ? 'Accepting…' : 'Accept invitation'}
                </Button>
              </div>
            </section>
          )}

          {state.kind === 'accepted' && (
            <section data-testid="join-accepted">
              <p style={bodyText}>
                You&rsquo;re in{state.roleName ? <> as <strong>{state.roleName}</strong></> : null}.
              </p>
              {/* A link that has to look like a button: buttonStyle() is
                  exported for exactly this, so it matches <Button> exactly. */}
              <div style={actions}>
                <a href="/home" data-testid="join-continue" style={buttonStyle('primary')}>
                  Continue to AutoeCTD
                </a>
              </div>
            </section>
          )}

          {state.kind === 'signed-out' && (
            <section data-testid="join-signed-out">
              <p style={bodyText}>Please sign in to accept this invitation.</p>
              <p style={mutedText}>
                Your invitation is saved — you will come back here straight after signing in.
              </p>
              <div style={actions}>
                <Button data-testid="join-signin-btn" onClick={signIn}>Sign in</Button>
              </div>
            </section>
          )}

          {state.kind === 'wrong-account' && (
            <section data-testid="join-wrong-account">
              <p style={bodyText}>
                This invitation was sent to{' '}
                <strong>{state.invitedEmail ?? UNNAMED_ADDRESS}</strong>, which is
                not the account you are signed in with.
              </p>
              <p style={mutedText}>
                Sign in with the invited account to accept it.
              </p>
              <div style={actions}>
                <Button variant="secondary" data-testid="join-switch-btn" onClick={signOut}>
                  Sign out and switch account
                </Button>
              </div>
            </section>
          )}

          {state.kind === 'unverified' && (
            <section data-testid="join-unverified">
              <p style={bodyText}>We could not verify this account against the invitation.</p>
              <p style={mutedText}>
                Ask your administrator to check the invitation.
              </p>
            </section>
          )}

          {state.kind === 'expired' && (
            <section data-testid="join-expired">
              <p style={bodyText}>This invitation has expired.</p>
              <p style={mutedText}>Ask an administrator to send a new one.</p>
            </section>
          )}

          {state.kind === 'gone' && (
            <section data-testid="join-gone">
              <p style={bodyText}>This invitation is no longer available.</p>
              <p style={mutedText}>
                It may already have been used or withdrawn. Ask an administrator for a new one.
              </p>
            </section>
          )}

          {state.kind === 'switched-off' && (
            <section data-testid="join-switched-off">
              <p style={bodyText}>Invitations are not open yet.</p>
              <p style={mutedText}>
                Invitations are switched off until the invite-acceptance feature is finished.
              </p>
            </section>
          )}

          {state.kind === 'error' && (
            <section data-testid="join-error">
              <p style={bodyText}>{state.message}</p>
              <div style={actions}>
                {/* Retries whichever call failed: looking again, or joining. */}
                <Button
                  data-testid="join-retry-btn"
                  onClick={() => { void (state.retry === 'accept' ? accept() : preview()); }}
                >
                  Try again
                </Button>
              </div>
            </section>
          )}
        </section>
      </main>
    </>
  );
}
